import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Variety {
  id: string;
  name: string; // Full variety name (e.g., "Pinot Noir")
  code: string; // Short code (e.g., "PN") for lot ID coding
  color: string | null; // Hex color code (e.g., "#6B4070") for UI display
  is_active: boolean;
  org_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateVarietyInput {
  name: string;
  code: string;
  color?: string;
  is_active?: boolean;
}

export interface UpdateVarietyInput {
  name?: string;
  code?: string;
  color?: string | null;
  is_active?: boolean;
}

/**
 * Hook to fetch all varieties for the current organization
 * @param activeOnly - If true, only returns active varieties (default: true)
 * @param searchTerm - Optional search term to filter by name
 */
export function useVarieties(activeOnly: boolean = true, searchTerm?: string) {
  return useQuery({
    queryKey: ["varieties", activeOnly, searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("variety")
        .select("*")
        .order("name", { ascending: true });

      if (activeOnly) {
        query = query.eq("is_active", true);
      }

      if (searchTerm) {
        query = query.ilike("name", `%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Variety[];
    },
  });
}

/**
 * Hook to fetch a single variety by ID
 */
export function useVariety(id: string | undefined) {
  return useQuery({
    queryKey: ["variety", id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("variety")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as Variety;
    },
    enabled: !!id,
  });
}

/**
 * Hook to create a new variety
 */
export function useCreateVariety() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateVarietyInput) => {
      // Get org_id from current user
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("variety")
        .insert([{
          name: input.name,
          code: input.code.toUpperCase(),
          color: input.color || null,
          is_active: input.is_active ?? true,
          org_id: userData.org_id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as Variety;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["varieties"] });
      toast({
        title: "Variety created",
        description: "The variety has been successfully created.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating variety",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to update an existing variety
 */
export function useUpdateVariety() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateVarietyInput }) => {
      const updateData: UpdateVarietyInput = { ...input };
      if (updateData.code) {
        updateData.code = updateData.code.toUpperCase();
      }

      const { data, error } = await supabase
        .from("variety")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Variety;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["varieties"] });
      queryClient.invalidateQueries({ queryKey: ["variety"] });
      toast({
        title: "Variety updated",
        description: "The variety has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating variety",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to delete a variety (or deactivate it)
 */
export function useDeleteVariety() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Soft delete by setting is_active to false
      const { error } = await supabase
        .from("variety")
        .update({ is_active: false })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["varieties"] });
      toast({
        title: "Variety deactivated",
        description: "The variety has been deactivated and will no longer appear in dropdowns.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deactivating variety",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to get varieties formatted for Select/Dropdown components
 * Returns { value: id, label: "Name (CODE)" }
 */
export function useVarietyOptions() {
  const { data: varieties, isLoading, error } = useVarieties(true);

  const options = varieties?.map((variety) => ({
    value: variety.id,
    label: `${variety.name} (${variety.code})`,
    name: variety.name,
    code: variety.code,
    color: variety.color,
  })) ?? [];

  return { options, isLoading, error };
}
